import { t } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import type { MailMessageDetail } from "./queries";
import { recipientsLabel, replySubject, senderLabel } from "./queries";

/**
 * What the composer opens with — kept apart from the composer itself (D41).
 *
 * The editor it carries is a third of the SPA's code, so `MailComposer` is
 * loaded only when a seller opens it (`lazy` in its two callers). A seed is a
 * plain value and a type: importing *this* costs nothing and does not drag the
 * editor back into the first page everyone downloads.
 */
export type ComposeSeed = {
  to: string;
  cc: string;
  subject: string;
  /** HTML the message box starts with. A forward opens with the original in it. */
  body: string;
  repliesTo: number | null;
  /** The message being forwarded. Its files are copied when it is sent. */
  forwards: number | null;
  /** A draft being reopened from *Borradores* (D39); its content is fetched. */
  draftId?: number | null;
};

export const BLANK: ComposeSeed = {
  to: "",
  cc: "",
  subject: "",
  body: "",
  repliesTo: null,
  forwards: null,
};

/** Reopening a draft: the composer is the reading pane for *Borradores* (D39). */
export function draftSeed(id: number): ComposeSeed {
  return { ...BLANK, draftId: id };
}

/**
 * Forward: nobody addressed yet, and the original **in the box**.
 *
 * Until 2026-10-23 the quoted original was appended by the worker at send time,
 * which left the seller writing above text they could not see. They forward a
 * message they sent a client, change a price, drop the forward headers and send
 * it — sometimes several times over — so the thing being forwarded has to be
 * editable (D41). What the box shows is now the whole body: the server appends
 * nothing, and only the files are still copied by `outbound.prepare_forward`.
 */
export function forwardSeed(message: MailMessageDetail): ComposeSeed {
  const subject = message.subject.trim();
  return {
    ...BLANK,
    subject: /^fwd:/i.test(subject) ? subject : `Fwd: ${subject}`,
    body: quotedOriginal(message),
    forwards: message.id,
  };
}

/** Reply to one person, or to everyone who was on the message. */
export function replySeed(message: MailMessageDetail, all: boolean): ComposeSeed {
  const others = all
    ? [...message.to, ...message.cc].map((recipient) => recipient.address)
    : [];
  return {
    ...BLANK,
    to: message.from_address,
    // The sender is already in `to`; leaving them in `cc` as well mails them
    // twice and shows the client a thread that looks duplicated.
    cc: others.filter((address) => address !== message.from_address).join(", "),
    subject: replySubject(message.subject),
    repliesTo: message.id,
  };
}

/**
 * The original under the line every mail client draws, as the editor's HTML.
 *
 * An empty paragraph first, which is where the cursor lands and the seller
 * writes. Then the header block every recipient expects of a forward — and
 * which this seller is free to delete, because that is the point.
 *
 * Everything that came from the message is escaped here. The body is not: it
 * was sanitised by `nh3` when the server stored it (§8.4), and escaping it
 * would forward a client's email as a wall of visible tags.
 */
function quotedOriginal(message: MailMessageDetail): string {
  const rows: [string, string][] = [
    [t("mail.from"), `${senderLabel(message)} <${message.from_address}>`],
    [t("mail.date"), formatDateTime(message.occurred_at)],
    [t("mail.to"), recipientsLabel(message.to)],
    [t("mail.subject"), message.subject],
  ];
  const header = rows
    .map(([name, value]) => `<b>${escapeHtml(name)}:</b> ${escapeHtml(value)}`)
    .join("<br>");
  return [
    "<p></p>",
    "<hr>",
    `<p>${escapeHtml(t("mail.forwardedBanner"))}</p>`,
    `<p>${header}</p>`,
    message.body_html,
  ].join("");
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
